import { get } from 'lodash';
import { all, call, put, select, take, takeEvery } from 'redux-saga/effects';

import { INVITATION_STATUS, REDUX_STORE } from '@funnels/constants';
import {
  CancelInvitationAction,
  CreateInvitationAction,
  FetchInvitationsAction,
  ReduxStoreData,
  StoreInvitation,
} from '@funnels/types';
import { datadogUtil } from '@funnels/utils';

import { waitForAuth } from '../_common';
import { updateApplicantInvitationStatus } from '../applicants/actions';
import {
  cancelInvitationFail,
  cancelInvitationSuccess,
  createInvitationFail,
  createInvitationSuccess,
  fetchInvitationsFail,
  fetchInvitationsSuccess,
  setInvitationError,
} from './actions';
import InvitationAPI from './api';

function* waitFroCancellation(): any {
  if (!(yield select((state: ReduxStoreData) => get(state, 'invitations.cancellation', 0)))) return;
  while (true) {
    yield take([
      REDUX_STORE.INVITATIONS.ACTION_TYPES.CANCEL_INVITATION_FAIL,
      REDUX_STORE.INVITATIONS.ACTION_TYPES.CANCEL_INVITATION_SUCCESS,
    ]);
    if (!(yield select((state: ReduxStoreData) => get(state, 'invitations.cancellation', 0)))) return;
  }
}

function* handleCancelInvitationSuccess(invitationId: string) {
  yield put(cancelInvitationSuccess());
  const reduxStoreInvitations: StoreInvitation[] = yield select((state: ReduxStoreData) => state.invitations.data);
  const cancelledInvitation = reduxStoreInvitations.find((invitation) => invitation.id === invitationId);

  if (cancelledInvitation?.applicantId) {
    yield put(updateApplicantInvitationStatus(cancelledInvitation.applicantId, INVITATION_STATUS.CANCELLED));
  }
}

export function* cancelInvitationWorker(action: CancelInvitationAction): any {
  try {
    const { invitationId, unauthorised } = action;

    yield call(InvitationAPI.cancelInvitation, invitationId, unauthorised);

    yield call(handleCancelInvitationSuccess, invitationId);
  } catch (e: any) {
    // If invite is already cancelled, we treat it as a "success"
    if (
      e?.response?.status === 422 &&
      typeof e?.response?.data === 'string' &&
      e.response.data.includes('already has status: CANCELLED')
    ) {
      yield call(handleCancelInvitationSuccess, action.invitationId);
      return;
    }

    datadogUtil.addError(e);
    yield all([put(cancelInvitationFail()), put(setInvitationError(e.response?.data || e.message))]);
  }
}

export function* createInvitationWorker(action: CreateInvitationAction): any {
  try {
    yield waitForAuth();
    yield waitFroCancellation();
    const { invitation } = action;
    const { applicationId, applicantId, firstName, mobile, clientId } = invitation;

    const response = yield call(InvitationAPI.createInvitation, applicationId, applicantId, firstName, mobile);

    yield put(createInvitationSuccess(clientId, response.data.id));

    yield put(updateApplicantInvitationStatus(applicantId, INVITATION_STATUS.PENDING));
  } catch (e: any) {
    datadogUtil.addError(e);
    yield all([put(createInvitationFail()), put(setInvitationError(e.response?.data || e.message))]);
  }
}

export function* fetchInvitationsWorker(action: FetchInvitationsAction): any {
  try {
    yield waitForAuth();
    const { applicationId } = action;

    const response = yield call(InvitationAPI.fetchInvitations, applicationId);

    yield put(fetchInvitationsSuccess(response.data));
  } catch (e: any) {
    datadogUtil.addError(e);
    yield all([put(fetchInvitationsFail()), put(setInvitationError(e.response?.data || e.message))]);
  }
}

export function* customerInviteWatcher() {
  yield takeEvery(REDUX_STORE.INVITATIONS.ACTION_TYPES.CANCEL_INVITATION, cancelInvitationWorker);
  yield takeEvery(REDUX_STORE.INVITATIONS.ACTION_TYPES.CREATE_INVITATION, createInvitationWorker);
  yield takeEvery(REDUX_STORE.INVITATIONS.ACTION_TYPES.FETCH_INVITATIONS, fetchInvitationsWorker);
}

export default customerInviteWatcher;
